
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import ShippingLabels from './ShippingLabels.vue';
import { hasRole, WellKnownRoles } from '@/models/User';

export default Vue.extend({
  name: 'GlobalShippingDialog',
  components: {
    ShippingLabels
  },
  data() {
    return {
      showSuccess: false,
      lastTrackingNumber: null as string | null,
      hasUnsavedChanges: false
    };
  },
  computed: {
    ...mapGetters('shippingDialog', [
      'isDialogOpen',
      'currentOrderId',
      'currentPickTicketId',
      'isLoading',
      'dialogError'
    ]),
    isOpen: {
      get(): boolean {
        return this.isDialogOpen;
      },
      set(value: boolean) {
        if (!value) {
          this.closeShippingDialog();
        }
      }
    },
    orderId(): number | null {
      return this.currentOrderId;
    },
    pickTicketId(): number | null {
      return this.currentPickTicketId;
    },
    error(): string | null {
      return this.dialogError;
    },
    isShipperMode(): boolean {
      const user = this.$store.state.authentication?.user;
      return user ? hasRole(user, WellKnownRoles.SHIPPER) : false;
    }
  },
  mounted() {
    // Listen for label created events from child components
    this.$bus.$on('shipping-label-form-changed', this.onFormChanged);
  },
  beforeDestroy() {
    this.$bus.$off('shipping-label-form-changed', this.onFormChanged);
  },
  methods: {
    ...mapActions('shippingDialog', [
      'closeShippingDialog',
      'addLabelToHistory'
    ]),
    
    handleClose() {
      // Check if there are unsaved changes
      if (this.hasUnsavedChanges) {
        const confirmed = window.confirm('Are you sure you want to close? Any unsaved changes will be lost.');
        if (!confirmed) {
          return;
        }
      }
      // Only reset form state — don't touch showSuccess/lastTrackingNumber
      // so the success snackbar persists after the dialog closes.
      this.hasUnsavedChanges = false;
      this.closeShippingDialog();
    },

    onFormChanged(hasChanges: boolean) {
      this.hasUnsavedChanges = hasChanges;
    },

    onLabelCreated(labelData: any) {
      console.log('[GlobalShippingDialog] Label created:', labelData);

      // Extract tracking number from various possible formats
      const trackingNumber = labelData.label?.trackingNumber ||
                           labelData.label?.tracking_number ||
                           labelData.trackingNumber ||
                           labelData.tracking_number ||
                           labelData.data?.trackingNumber ||
                           labelData.data?.tracking_number;

      this.lastTrackingNumber = trackingNumber;

      // Add to history
      this.addLabelToHistory({
        pickTicketId: this.pickTicketId,
        trackingNumber: trackingNumber,
        orderId: this.orderId,
        labelData: labelData
      });

      // Show success snackbar (persists for 5s after dialog closes)
      this.showSuccess = true;

      // Emit global event for other components to listen to
      this.$bus.$emit('shipping-label-created', {
        orderId: this.orderId,
        pickTicketId: this.pickTicketId,
        trackingNumber: trackingNumber,
        labelData: labelData
      });

      // Emit event for order status page refresh (pick tickets, line items, etc.)
      this.$bus.$emit('print-pickTicket');

      // Also emit specific event for order status update
      this.$bus.$emit('pick-ticket-shipped', {
        pickTicketId: this.pickTicketId,
        trackingNumber: trackingNumber
      });
    },

    onError(error: any) {
      console.error('[GlobalShippingDialog] Shipping label error:', error);
      const errorMessage = error.message || error.error || 'Failed to create shipping label';
      this.$toast?.error(errorMessage);
    },

    onSuccess(message: string) {
      this.$toast?.success(message);
    }
  }
});
